import { ApolloQueryResult } from "@apollo/client"
import { Delete, FactCheck } from "@mui/icons-material"
import { Grid, IconButton, Tooltip } from "@mui/material"
import { Form, Formik } from "formik"
import { SurveyRecord, SurveyRecordQuery, SurveyRecordVerification as SurveyRecordVerificationType, useDeleteOneSurveyRecordVerificationMutation, useUpdateOneSurveyRecordVerificationMutation } from "../../generated/graphql"
import useCustomModal from "../../hooks/useCustomModal"
import { useGtXs } from "../../hooks/useGtXs"
import useToast from "../../hooks/useToast"
import AutoSave from "../AutoSave/AutoSave"
import ConfirmButton from "../ConfirmButton/ConfirmButton"
import CustomModal from "../CustomModal/CustomModal"
import InputAutoTextField from "../InputAutoTextField/InputAutoTextField"
import Show from "../Show/Show"
import SurveyRecordVerificationClassificationField from "../SurveyRecordVerificationClassificationField/SurveyRecordVerificationClassificationField"
import SurveyRecordVerificationEvidence from "../SurveyRecordVerificationEvidence/SurveyRecordVerificationEvidence"
import { SurveyRecordVerificationStateDisplay } from "../SurveyRecordVerificationState/SurveyRecordVerificationState"
import VerificationField from "../VerificationField/VerificationField"
import styles from './SurveyRecordVerification.module.scss'
import surveyRecordVerificationFormInit from "./SurveyRecordVerificationFormInit"

interface SurveyRecordVerificationProps {
    surveyRecord: SurveyRecord
    surveyRecordVerification: SurveyRecordVerificationType
    refetch: () => Promise<ApolloQueryResult<SurveyRecordQuery>>
    isNotUserEdit: boolean
    allowed: boolean
}

export default function SurveyRecordVerification(props: SurveyRecordVerificationProps) {
    const { surveyRecord, surveyRecordVerification, refetch, isNotUserEdit, allowed } = props

    const { schema, initial } = surveyRecordVerificationFormInit(surveyRecordVerification)
    const { feedback } = useToast()
    const [update] = useUpdateOneSurveyRecordVerificationMutation()
    const [deleteSurveyRecordVerification] = useDeleteOneSurveyRecordVerificationMutation()
    const gtXs = useGtXs()
    const { state, close, open } = useCustomModal()

    const handleSubmit = async (values: any) => {
        if (initial && values && initial !== values) {
            const { id, description, surveyRecordVerificationState, verification, surveyRecordVerificationClassification } = values
            if (id) {
                const response = await update({
                    variables: {
                        input: {
                            id, update: {
                                verification: verification?.id,
                                description,
                                surveyRecordVerificationClassification: surveyRecordVerificationClassification?.id,
                                surveyRecordVerificationState: surveyRecordVerificationState?.id
                            }
                        }
                    }
                })
                feedback(!response?.errors?.length, "Linha criada com sucesso", "Erro ao criar linha")
                refetch()
            }
        }
    }

    const handleDelete = async () => {
        const { id } = surveyRecordVerification
        const response = await deleteSurveyRecordVerification({ variables: { input: { id } } })
        feedback(!response?.errors?.length, "Linha removida com sucesso", "Erro ao remover linha")
        refetch()
    }

    return (
        <Formik initialValues={initial} onSubmit={handleSubmit} validationSchema={schema}>
            {({ values }) => {
                return (
                    <div>
                        <Form>
                            <Grid container direction={gtXs ? "row" : "column"} className={styles.SurveyRecordVerification} alignItems={gtXs ? "flex-end" : "flex-start"}>
                                <span style={{ minWidth: 120, maxWidth: 120, marginRight: 30 }}></span>
                                <span style={{ minWidth: 250, maxWidth: 250, marginRight: 30, marginBottom: 10 }}>
                                    <VerificationField disabled={true} />
                                </span>
                                <span style={{ minWidth: gtXs ? 600 : 250, marginRight: 30, marginBottom: 10 }}>
                                    <InputAutoTextField
                                        name="description"
                                        multiline
                                        minRows={1}
                                        maxRows={5}
                                        label="descrição"
                                        fullWidth
                                        disabled={isNotUserEdit || !allowed} />

                                </span>

                                <span style={{ minWidth: 250, maxWidth: 250, marginRight: 30, marginBottom: 10 }}>
                                    <SurveyRecordVerificationClassificationField disabled={isNotUserEdit || !allowed} />
                                </span>

                                <Show on={gtXs && values?.surveyRecordVerificationClassification?.isCorrectiveActionRequest}>
                                    <>
                                        <span style={{ minWidth: 100, maxWidth: 100, marginRight: 30, marginBottom: 10 }}>
                                            <SurveyRecordVerificationStateDisplay
                                                state={surveyRecordVerification?.surveyRecordVerificationState}
                                            />
                                        </span>

                                        <span>
                                            <Tooltip title="Evidência">
                                                <IconButton onClick={open}>
                                                    <FactCheck color={surveyRecordVerification?.surveyRecordVerificationPhotos?.length ? "primary" : "secondary"} />
                                                </IconButton>
                                            </Tooltip>
                                        </span>
                                    </>
                                </Show>

                                <Show on={!gtXs && values?.surveyRecordVerificationClassification?.isCorrectiveActionRequest}>
                                    <Grid container justifyContent="space-between" alignItems="center" style={{ marginBottom: 10 }}>
                                        <div>
                                            <SurveyRecordVerificationStateDisplay
                                                state={surveyRecordVerification?.surveyRecordVerificationState}
                                            />
                                        </div>
                                        <div style={{ marginRight: -10 }}>
                                            <IconButton onClick={open} size="small">
                                                <FactCheck color={surveyRecordVerification?.surveyRecordVerificationPhotos?.length ? "primary" : "secondary"} />
                                            </IconButton>
                                        </div>
                                    </Grid>
                                </Show>

                                <span className={styles.RemoveVerification}>
                                    <ConfirmButton onClick={handleDelete} tooltip="Remover Verificação" disabled={isNotUserEdit || !allowed} >
                                        <Delete />
                                    </ConfirmButton>
                                </span>

                                <AutoSave />
                            </Grid>
                        </Form>
                        <CustomModal isActive={state} handleClose={close} fullScreen>
                            <SurveyRecordVerificationEvidence
                                surveyRecord={surveyRecord}
                                surveyRecordVerification={surveyRecordVerification}
                                refetch={refetch}
                            />
                        </CustomModal>
                    </div>
                )
            }}
        </Formik>
    )
}
